import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { documentsAPI, workflowsAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';
import RichContentView from '../components/RichContentView';
import RedlineView, { authorColorVar } from '../components/RedlineView';
import AttachmentsPanel from '../components/AttachmentsPanel';
import ContributorsPanel from '../components/ContributorsPanel';
import CommentsPanel from '../components/CommentsPanel';
import ChangeModeToggle from '../components/ChangeModeToggle';
import ChangeLogList from '../components/ChangeLogList';
import SubmitForApprovalModal from '../components/SubmitForApprovalModal';
import ApprovalActionBar from '../components/ApprovalActionBar';
import TypeEvaluationPanel from '../components/TypeEvaluationPanel';
import CorpusScanPanel from '../components/CorpusScanPanel';
import BookmarkButton from '../components/BookmarkButton';
import AcknowledgementBar from '../components/AcknowledgementBar';
import AcknowledgementSettingsModal from '../components/AcknowledgementSettingsModal';
import { useResourcePermissions } from '../auth/useResourcePermissions';
import { useDocumentViewRights } from '../auth/useDocumentViewRights';
import { recordRight } from '../auth/recordRights';

/**
 * Read-only document view -- previously the only per-document surfaces were
 * the list and `/edit`, and `/edit` dead-ends with "can no longer be edited"
 * for anything past Draft. A published policy was unreadable in the app that
 * manages it. This is also the first consumer of `GET /{id}/history` and
 * `GET /{id}/workflow-history`, both of which already existed and were
 * called from nowhere in the frontend.
 */
function DocumentViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Set by the links on ApprovalsPage. Only affects the back affordance: an
  // approver who arrived from their queue should land back in it, not in the
  // document list they never visited. Absent on a reload or a deep link, which
  // is why it decides nothing else.
  const cameFromApprovals = useLocation().state?.from === '/approvals';
  // Same rule, for the Document Insights Likeness Log (GitHub issue #163):
  // following a document from there should return there, not to the plain
  // Documents list the reader never visited.
  const cameFromInsights = useLocation().state?.from === '/insights';
  // Same rule again, for the Start Page's search (GitHub issue #173): a
  // document opened from a search result, an answer's citation, a related
  // document, or either widget should return to that exact search --
  // query, mode and filters intact, since it's all in the URL -- rather than
  // to the generic Documents list, which the reader may not even have a link
  // to (document_browse is removable per role) and which was never where they
  // were browsing from.
  const cameFromSearch = useLocation().state?.from === '/';
  // documents is HIERARCHICAL, so this is a HINT (any_scope=True) -- it means
  // "holds documents.update somewhere", not "at this document's node". `false`
  // is exact and safe to hide on; a `true` may still 403 on click, which the
  // server catches (GitHub issue #47).
  const { canUpdate: canUpdateDocument } = useResourcePermissions('documents');
  // Issue #173. A reader who reached this policy from the Start Page may have
  // no browse list to go back TO -- `document_browse` is removable per role --
  // so the back button points at the search instead. Sending them to a
  // "No access" page from a policy they are entitled to read would be a
  // strictly worse dead end than the one this replaces.
  const { canRead: canBrowseDocuments } = useResourcePermissions('document_browse');
  // Falls back to a plain link when there is no navigation history to unwind
  // (a reload, a bookmark, a pasted link) -- see the two render sites below,
  // which both prefer navigate(-1) over this whenever cameFromSearch/
  // cameFromApprovals/cameFromInsights says there is somewhere real to return to.
  const backTarget = canBrowseDocuments
    ? { to: '/documents', label: 'Back to Documents' }
    : { to: '/', label: '← Back to Search' };

  const [doc, setDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [workflowHistory, setWorkflowHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startingRevision, setStartingRevision] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  // A quick summary for the revision-in-progress banner: who's touched it and
  // how many open comment threads there are. Fetched separately from `doc`
  // because it's two more round trips only worth making when there IS an open
  // revision to summarize.
  const [revisionSummary, setRevisionSummary] = useState(null);
  const [expandedVersionId, setExpandedVersionId] = useState(null);
  // { [versionId]: { kind: 'approved' | 'save', ... } }. Two kinds because the
  // table asks two different questions per row -- see handleToggleVersionRedline.
  const [versionChanges, setVersionChanges] = useState({});
  const [permalinkCopied, setPermalinkCopied] = useState(false);
  const [showAcknowledgementSettings, setShowAcknowledgementSettings] = useState(false);

  // Clean | Changes for a FIRST-CYCLE document -- one where the Document row
  // itself is what is under approval, so there is no revision to view and
  // RevisionViewPage never gets a chance to show this. Same default rule that
  // page owns (changes while drafting, clean once submitted), which is what
  // makes a send-back flip it back to changes for free: reject_step returns
  // document.status to draft, and this reads that.
  const [contentMode, setContentMode] = useState('changes');
  const [contentModeTouched, setContentModeTouched] = useState(false);
  const [documentRedline, setDocumentRedline] = useState(null);
  // Driven by the DOCUMENT'S OWN view_rights map once it has loaded (issue
  // #181), falling back to the global can() hint until then. Declared here
  // rather than at the top of the component because it needs `doc`.
  const viewRights = useDocumentViewRights(doc);
  // Per-record, against THIS document's node -- so an Edit button no longer
  // appears on a policy belonging to a department the viewer cannot touch.
  // Falls back to canUpdateDocument while the fetch is in flight.
  const mayEditDocument = recordRight(doc?.can_edit, canUpdateDocument);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      // The DOCUMENT is the only mandatory fetch. The other two are panels,
      // and since issue #138 version history can legitimately be refused --
      // letting a 403 there reject the whole Promise.all blanked the page and
      // rendered "Document not found" over a policy the viewer could read
      // perfectly well, which is why both carry `.catch(() => null)`.
      //
      // HISTORY IS NOW FETCHED UNCONDITIONALLY, and that is a deliberate
      // change of trade (issue #181). It used to be skipped when the global
      // can() hint said no, to save a round trip on a hidden panel -- but that
      // hint cannot see the server's exemptions (an approver on an open gate,
      // the owner, a contributor), so for exactly the people #181 exists to
      // fix it skipped a fetch the server would have answered, and the panel
      // would render empty. The document's own view_rights decides whether the
      // panel RENDERS, below; it cannot decide this, because it arrives in
      // this very response. The cost of being wrong the other way is one 403
      // on a detail page.
      const [docRes, historyRes, workflowRes] = await Promise.all([
        documentsAPI.get(id, 'view'),
        documentsAPI.getHistory(id).catch(() => null),
        documentsAPI.getWorkflowHistory(id).catch(() => null),
      ]);
      setDoc(docRes.data);
      setVersions(historyRes?.data || []);
      setWorkflowHistory(workflowRes?.data || null);
      setError(null);
      if (!contentModeTouched) {
        setContentMode(docRes.data.status === 'draft' ? 'changes' : 'clean');
      }

      if (docRes.data.open_revision_id) {
        const [redlineRes, commentsRes] = await Promise.all([
          docRes.data.open_revision_purpose === 'periodic_review'
            ? Promise.resolve(null)
            : documentsAPI.getRevisionRedline(id, docRes.data.open_revision_id).catch(() => null),
          documentsAPI.listComments(id, docRes.data.open_revision_id, false).catch(() => null),
        ]);
        setRevisionSummary({
          participants: redlineRes?.data?.participants || [],
          unresolvedComments: commentsRes?.data?.length || 0,
        });
      } else {
        setRevisionSummary(null);
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
    // viewRights is deliberately NOT a dependency any more. It now derives from
    // `doc`, which this function sets -- so depending on it would rebuild
    // fetchAll on every fetch and re-fetch the document forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, contentModeTouched]);

  // has_redline picks the SOURCE, it no longer gates the button. A row that
  // froze a redline at publish answers "what did the approvers sign off on"
  // from that stored copy; every other row answers "what did this one save
  // change" from the derived per-save endpoint. Both are real questions a
  // workflow validation session asks, and before this only the first row of a
  // published revision could answer either of them.
  const handleToggleVersionRedline = async (version) => {
    if (expandedVersionId === version.id) {
      setExpandedVersionId(null);
      return;
    }
    setExpandedVersionId(version.id);
    if (versionChanges[version.id]) return;
    try {
      if (version.has_redline) {
        const res = await documentsAPI.getVersionRedline(id, version.id);
        setVersionChanges((prev) => ({
          ...prev,
          [version.id]: { kind: 'approved', redlineMarkdown: res.data.redline_markdown },
        }));
      } else {
        const res = await documentsAPI.getVersionChanges(id, version.id);
        setVersionChanges((prev) => ({
          ...prev,
          [version.id]: { kind: 'save', ...res.data },
        }));
      }
    } catch (err) {
      console.error('Error loading version changes:', err);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // GET /{id}/redline: the first-cycle counterpart of the revision redline,
  // implemented and tested since the feature landed and called from nowhere
  // until now. Fetched lazily and only in changes mode -- a published policy
  // never asks for it.
  useEffect(() => {
    if (!doc || contentMode !== 'changes') return undefined;
    if (doc.status !== 'draft' && doc.status !== 'pending_approval') return undefined;
    let cancelled = false;
    documentsAPI.getDocumentRedline(id)
      .then((res) => { if (!cancelled) setDocumentRedline(res.data); })
      .catch((err) => console.error('Error loading document redline:', err));
    return () => { cancelled = true; };
  }, [id, doc, contentMode]);

  const handleStartRevision = async (purpose) => {
    setStartingRevision(true);
    setError(null);
    try {
      const res = await documentsAPI.createRevision(id, purpose);
      if (purpose === 'periodic_review') {
        // A periodic review proposes no change, so there is nothing to edit --
        // it goes straight to picking a workflow. (An author who decides changes
        // ARE needed converts it to a revision first; see RevisionEditPage.)
        await fetchAll();
        setShowSubmitModal(true);
      } else {
        navigate(`/documents/${id}/revisions/${res.data.id}/edit`);
      }
    } catch (err) {
      console.error('Error starting revision:', err);
      setError(extractErrorMessage(err));
    } finally {
      setStartingRevision(false);
    }
  };

  const handleWithdrawRevision = async () => {
    if (!window.confirm(
      'Withdraw this revision? The live policy is unaffected, but the draft is discarded '
      + 'and its version number is not reused.'
    )) return;
    try {
      await documentsAPI.withdrawRevision(id, doc.open_revision_id);
      fetchAll();
    } catch (err) {
      console.error('Error withdrawing revision:', err);
      setError(extractErrorMessage(err));
    }
  };

  const handleSubmitRevision = async (templateId) => {
    // templateId already arrives as null when the modal is resuming an
    // existing instance -- it computes that from the isResuming prop we pass
    // it below, same as the document's own first-cycle submit does.
    await workflowsAPI.submitRevisionForApproval(doc.open_revision_id, templateId);
    setShowSubmitModal(false);
    setError(null);
    fetchAll();
  };

  const handleDownload = async () => {
    try {
      const response = await documentsAPI.download(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.title);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download file');
      console.error('Download error:', err);
    }
  };

  const handleCopyPermalink = async () => {
    // /documents/:id is already a permalink by construction -- the id never
    // changes and this route always renders the current live Document row,
    // never a specific version or an in-progress revision (see the
    // versioning invariant in CLAUDE.md: Document.content always holds what
    // a reader sees). This button exists purely so that URL is easy to grab
    // and hand to someone, e.g. for a regulator or an internal reference.
    const url = `${window.location.origin}/documents/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setPermalinkCopied(true);
      setTimeout(() => setPermalinkCopied(false), 2000);
    } catch (err) {
      console.error('Error copying permalink:', err);
      window.prompt('Copy this link:', url);
    }
  };

  if (loading) {
    return <div className="loading">Loading document...</div>;
  }

  if (!doc) {
    return (
      <div>
        {error && <div className="alert alert-error">{error}</div>}
        <div className="alert alert-error">Document not found</div>
        {cameFromSearch ? (
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            ← Back to Search
          </button>
        ) : (
          <Link to={backTarget.to} className="btn btn-secondary">{backTarget.label}</Link>
        )}
      </div>
    );
  }

  // The document row itself is still what gets approved -- no revision exists
  // yet, so its own drafts ARE its edit history and a redline of them is the
  // only tracked-changes view there is.
  const firstCycle = doc.status === 'draft' || doc.status === 'pending_approval';

  return (
    <div>
      <div className="page-header">
        <h2>📄 {doc.title}</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={handleCopyPermalink}>
            {permalinkCopied ? '✅ Copied!' : '🔗 Copy Permalink'}
          </button>
          <BookmarkButton documentId={id} />
          {/* Config for whoever may edit the policy. Deliberately here rather
              than in DocumentForm, which is draft-only: switching attestation
              on for an already-published policy is the normal case. */}
          {mayEditDocument && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowAcknowledgementSettings(true)}
            >
              ✍️ Acknowledgement
            </button>
          )}
          {/* Only offered on a live policy -- a DRAFT's content isn't the
              policy of record yet, and the backend 400s for anything but
              ACTIVE (GitHub #43). Lazy-generated and cached server-side on
              first click; see documentsAPI.printableUrl. */}
          {doc.status === 'active' && (
            <a className="btn btn-secondary" href={documentsAPI.printableUrl(id)}
               target="_blank" rel="noopener noreferrer">
              🖨️ Printable
            </a>
          )}
          {mayEditDocument && doc.status === 'draft' && (
            <Link to={`/documents/${id}/edit`} className="btn btn-primary">✏️ Edit</Link>
          )}
          {/* Both are only offered on a live policy, and only when there is no
              proposal already in flight -- the backend allows one at a time. */}
          {mayEditDocument && doc.status === 'active' && !doc.open_revision_id && (
            <>
              <button className="btn btn-primary" onClick={() => handleStartRevision('revision')}
                      disabled={startingRevision}>
                {startingRevision ? '⏳ Starting...' : '✏️ Revise'}
              </button>
              <button className="btn btn-secondary" onClick={() => handleStartRevision('periodic_review')}
                      disabled={startingRevision}>
                🔄 Start Periodic Review
              </button>
            </>
          )}
          {cameFromApprovals ? (
            // navigate(-1) rather than a link to /approvals: it goes back to
            // the queue as the approver left it, group filter and all.
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>
              ← Back to Approvals
            </button>
          ) : cameFromInsights ? (
            // Same reasoning: back to the Likeness Log as it was left, filters
            // and all, rather than the plain Documents list (GitHub issue #163).
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>
              ← Back to Document Insights
            </button>
          ) : cameFromSearch ? (
            // Same reasoning again (issue #173): back to the Start Page exactly
            // as the reader left it -- question, mode, filters -- rather than to
            // /documents, which they may hold no grant for at all.
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>
              ← Back to Search
            </button>
          ) : (
            <Link to={backTarget.to} className="btn btn-secondary">{backTarget.label}</Link>
          )}
        </div>
      </div>

      {/* Renders nothing unless this caller holds an open gate on this
          document -- server-decided, so a reload or a shared link behaves the
          same as a click-through from the queue. */}
      <ApprovalActionBar
        documentId={id}
        onActed={fetchAll}
        afterActionTo={cameFromApprovals ? '/approvals' : null}
      />

      {/* Renders nothing unless this policy requires an attestation AND this
          viewer is in its audience -- derived server-side from their profile
          default locations, so there is nothing to guess at here. */}
      <AcknowledgementBar documentId={id} />

      {showAcknowledgementSettings && (
        <AcknowledgementSettingsModal
          documentId={id}
          onClose={() => setShowAcknowledgementSettings(false)}
        />
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {doc.open_revision_id && (
        <div className="alert alert-info" role="status">
          <strong>
            ↻ {doc.open_revision_purpose === 'periodic_review'
              ? 'Periodic review in progress'
              : `Revision ${doc.open_revision_version_label} in progress`}
          </strong>
          {' — '}
          {doc.open_revision_status === 'pending_approval'
            ? 'awaiting approval.'
            : 'still a draft.'}
          {' '}The version below is the one currently in force and stays live until the
          revision is approved.
          {revisionSummary && revisionSummary.participants.length > 0 && (
            <div className="redline-participants" style={{ marginTop: '8px', marginBottom: 0 }}>
              {revisionSummary.participants.map((p) => (
                <span key={p.author_key || 'unknown'} className="redline-participant">
                  <span className="redline-participant-dot"
                        style={{ '--redline-author-color': `var(${authorColorVar(p.author_key) || '--muted'})` }} />
                  {p.author_name || p.author_key || 'Unknown'}
                </span>
              ))}
              {revisionSummary.unresolvedComments > 0 && (
                <span className="redline-participant">
                  💬 {revisionSummary.unresolvedComments} open comment
                  {revisionSummary.unresolvedComments === 1 ? '' : 's'}
                </span>
              )}
              <Link to={`/documents/${id}/revisions/${doc.open_revision_id}`}
                    className="btn btn-secondary btn-small">
                View changes
              </Link>
            </div>
          )}
          {mayEditDocument && doc.open_revision_status === 'draft' && (
            <span style={{ marginLeft: '10px' }}>
              <Link to={`/documents/${id}/revisions/${doc.open_revision_id}/edit`}
                    className="btn btn-secondary btn-small">
                {doc.open_revision_purpose === 'periodic_review' ? 'Review' : 'Continue editing'}
              </Link>
              {doc.open_revision_purpose === 'revision' && (
                <button className="btn btn-success btn-small" style={{ marginLeft: '6px' }}
                        onClick={() => setShowSubmitModal(true)}>
                  {doc.open_revision_pending_workflow_instance_id
                    ? '📤 Resume Submission' : '📤 Submit for Approval'}
                </button>
              )}
              <button className="btn btn-danger btn-small" style={{ marginLeft: '6px' }}
                      onClick={handleWithdrawRevision}>
                Withdraw
              </button>
            </span>
          )}
        </div>
      )}

      {doc.last_return && (
        <div className="alert alert-warning" role="status">
          <strong>↩️ Returned by {doc.last_return.returned_by}</strong>
          {doc.last_return.impersonator_name && ` (via ${doc.last_return.impersonator_name})`}
          {doc.last_return.change_category_name && ` — ${doc.last_return.change_category_name}`}
          {doc.last_return.reason && <div style={{ marginTop: '4px' }}>{doc.last_return.reason}</div>}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>Details</h3>
          <span className={`badge badge-${doc.status}`}>{doc.status}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Location</div>
            <div>{doc.hierarchy_display_path}</div>
          </div>
          <div>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Version</div>
            <div>{doc.version_label}</div>
          </div>
          <div>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Created</div>
            <div>{new Date(doc.created_at).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Last Updated</div>
            <div>{new Date(doc.updated_at).toLocaleString()}</div>
          </div>
          {doc.expires_at && (
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Expires</div>
              <div>{new Date(doc.expires_at).toLocaleDateString()}</div>
            </div>
          )}
          {doc.review_frequency_name && (
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Review Frequency</div>
              <div>{doc.review_frequency_name} ({doc.review_frequency_years} yr)</div>
            </div>
          )}
          {doc.owner_name && (
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Owner</div>
              <div>{doc.owner_name}</div>
            </div>
          )}
          {doc.document_type_name && (
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Document Type</div>
              <div>{doc.document_type_name}</div>
            </div>
          )}
          {doc.document_template_name && (
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Template</div>
              <div>{doc.document_template_name}</div>
            </div>
          )}
          {doc.file_path && (
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Attachment</div>
              <button className="btn btn-secondary btn-small" onClick={handleDownload}>⬇️ Download</button>
            </div>
          )}
          {doc.policy_areas.length > 0 && (
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Policy Areas</div>
              <div>{doc.policy_areas.map(a => a.name).join(', ')}</div>
            </div>
          )}
          {doc.regulatory_references.length > 0 && (
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Regulatory References</div>
              <div>{doc.regulatory_references.map(r => r.name).join(', ')}</div>
            </div>
          )}
          {doc.applicability_groups.length > 0 && (
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>Applies To</div>
              <div>{doc.applicability_groups.map(g => g.name).join(', ')}</div>
            </div>
          )}
        </div>
      </div>

      {/* Each AI panel is separately grantable since issue #138. An AFFORDANCE
          ONLY -- the server re-checks, and its exemptions (an approver on an
          open gate, an editor, the owner) are not modelled client-side, so the
          honest failure here is a panel hidden from somebody the server would
          have admitted. */}
      {viewRights.can('type_check') && (
        <TypeEvaluationPanel documentId={id} canRun />
      )}
      {viewRights.can('similar_documents') && (
        <CorpusScanPanel documentId={id} canRun kind="duplicate_scan" />
      )}
      {viewRights.can('reuse_opportunities') && (
        <CorpusScanPanel documentId={id} canRun kind="reuse_candidates" />
      )}
      {viewRights.can('template_conformance') && (
        <CorpusScanPanel documentId={id} canRun kind="template_conformance" />
      )}

      <div className="card">
        <div className="card-header">
          <h3>Content</h3>
          {/* Only while the DOCUMENT ROW itself is what is under approval. Once
              it publishes, changes are proposed as revisions and belong to
              RevisionViewPage's own toggle. */}
          {firstCycle && (
            <ChangeModeToggle
              mode={contentMode}
              onChange={(m) => { setContentMode(m); setContentModeTouched(true); }}
            />
          )}
        </div>
        {firstCycle && contentMode === 'changes' ? (
          documentRedline ? (
            <>
              {documentRedline.participants.length > 0 && (
                <div className="redline-participants">
                  {documentRedline.participants.map((p) => (
                    <span key={p.author_key || 'unknown'} className="redline-participant">
                      <span className="redline-participant-dot"
                            style={{ '--redline-author-color': `var(${authorColorVar(p.author_key) || '--muted'})` }} />
                      {p.author_name || p.author_key || 'Unknown'}
                      {' '}(+{p.inserted_tokens} / −{p.deleted_tokens})
                    </span>
                  ))}
                </div>
              )}
              <RedlineView redlineMarkdown={documentRedline.redline_markdown} colorByAuthor />
            </>
          ) : (
            <div className="loading">Loading changes...</div>
          )
        ) : (
          <RichContentView content={doc.content} />
        )}
      </div>

      <AttachmentsPanel documentId={id} editable={false} />

      {/* Similar to Owner, but per-document (issue #132). Same per-record
          flag the Edit button above uses (issue #181) -- so it no longer
          offers to manage contributors on a document at a node the viewer
          holds nothing at. The server is still the actual control
          (contributor_service.assert_may_manage). */}
      <ContributorsPanel documentId={id} canManage={mayEditDocument} />

      {/* Separately grantable since issue #138. An affordance only -- an
          approver on an open gate reaches the thread server-side without this
          grant, which is what stops "you must comment on this" and "you may not
          see it" from both being true at once. */}
      {viewRights.can('comments') && (
        <div className="card">
          {/* Whether the COMPOSER is shown is a separate, server-computed
              question from whether the thread is readable above (issue #210) --
              see schemas.DocumentDetailResponse.can_comment. The `true`
              fallback is the pre-#210 behaviour exactly, so a response without
              the field regresses nothing. */}
          <CommentsPanel documentId={id} canComment={recordRight(doc?.can_comment, true)} />
        </div>
      )}

      {viewRights.can('versions') && (
      <div className="card">
        <div className="card-header"><h3>Version History ({versions.length})</h3></div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Version</th>
                <th>Changed By</th>
                <th>Summary</th>
                <th>Changed At</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {versions.map(v => (
                <React.Fragment key={v.id}>
                  <tr>
                    <td>{v.version_label}</td>
                    <td>
                      {v.changed_by_user_id || '—'}
                      {/* Written on every save since Tier 3 / issue #44 and
                          read back nowhere until now. A validation session
                          needs the real human, not the account. */}
                      {v.changed_by_impersonator_email && (
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          via {v.changed_by_impersonator_email}
                        </div>
                      )}
                      {v.changed_by_on_behalf_of_email && (
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                          on behalf of {v.changed_by_on_behalf_of_email}
                        </div>
                      )}
                    </td>
                    <td>{v.change_summary || '—'}</td>
                    <td>{new Date(v.changed_at).toLocaleString()}</td>
                    <td>
                      <button type="button" className="btn-link"
                              onClick={() => handleToggleVersionRedline(v)}>
                        {expandedVersionId === v.id
                          ? 'Hide'
                          : (v.has_redline ? 'Approved changes' : 'What changed')}
                      </button>
                    </td>
                  </tr>
                  {expandedVersionId === v.id && (
                    <tr>
                      <td colSpan={5}>
                        <VersionChangesPanel data={versionChanges[v.id]} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {workflowHistory && workflowHistory.instances.length > 0 && (
        <div className="card">
          <div className="card-header"><h3>Approval Trail</h3></div>
          {workflowHistory.instances.map(instance => (
            <div key={instance.id} style={{ marginBottom: '18px' }}>
              <div style={{ fontWeight: 700, marginBottom: '8px' }}>
                {/* purpose distinguishes "signed off on brand-new text" from
                    "re-confirmed with no changes" -- the version label only
                    exists for a revision, since a review that stays a review
                    never gets one (decision: no-change reviews don't move
                    the version). */}
                {instance.purpose === 'periodic_review'
                  ? '🔄 Periodic Review'
                  : instance.purpose === 'revision'
                    ? `✏️ Revision ${instance.revision_version_label || ''}`
                    : '🆕 Initial Approval'}
                {' '}
                <span style={{ fontSize: '14px', fontWeight: 400, color: 'var(--muted)' }}>
                  {instance.template_name}
                </span>
                {' '}<span className="badge">{instance.status}</span>
              </div>
              {instance.steps.map(step => (
                <div key={step.id} style={{ marginLeft: '12px', marginBottom: '10px' }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 600 }}>
                    Gate {step.step_order}: {step.group_name}
                    {step.cycle_number > 1 && ` (cycle ${step.cycle_number})`}
                    {' — '}<span style={{ color: 'var(--muted)' }}>{step.status}</span>
                  </div>
                  {step.actions.map(action => (
                    <div key={action.id} style={{ marginLeft: '14px', fontSize: '13px', color: 'var(--ink-soft)' }}>
                      {action.action === 'reject' ? '↩️' : '✓'} {action.member_name}
                      {action.impersonator_name && ` (via ${action.impersonator_name})`}
                      {action.change_category_name && ` — ${action.change_category_name}`}
                      {action.comment && `: ${action.comment}`}
                      {' — '}{new Date(action.acted_at).toLocaleString()}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {showSubmitModal && (
        <SubmitForApprovalModal
          title={doc.open_revision_purpose === 'periodic_review'
            ? 'Start Periodic Review' : `Submit Revision ${doc.open_revision_version_label}`}
          hierarchyNodeId={doc.hierarchy_node_id}
          documentId={doc.id}
          isResuming={!!doc.open_revision_pending_workflow_instance_id}
          pendingTemplateName={doc.open_revision_pending_workflow_template_name}
          onSave={handleSubmitRevision}
          onClose={() => setShowSubmitModal(false)}
        />
      )}
    </div>
  );
}

/**
 * One expanded Version History row.
 *
 * `approved` renders the frozen redline in full through RedlineView -- that is
 * the "read what the approvers signed off on" case the component exists for.
 *
 * `save` renders ChangeLogList instead, and that is issue #37's reasoning
 * applied verbatim rather than forgotten: one save on a long policy is a couple
 * of edits, and re-rendering the whole policy per row is exactly what that
 * issue reported. The header above it is what makes the row stand alone as an
 * artifact of a validation session -- who, when, under whose authority, and how
 * much moved.
 */
function VersionChangesPanel({ data }) {
  if (!data) return <div className="loading">Loading…</div>;

  if (data.kind === 'approved') {
    return <RedlineView redlineMarkdown={data.redlineMarkdown} colorByAuthor />;
  }

  const { stats } = data.redline;
  return (
    <div>
      <div className="changelog-meta" style={{ marginBottom: '10px' }}>
        <strong style={{ color: 'var(--ink)' }}>
          {data.author_name || data.author_key || 'Unknown author'}
        </strong>
        {data.impersonator_email && ` · via ${data.impersonator_email}`}
        {data.on_behalf_of_email && ` · on behalf of ${data.on_behalf_of_email}`}
        {' · '}{new Date(data.changed_at).toLocaleString()}
        {' · '}+{stats.inserted_tokens} / −{stats.deleted_tokens}
      </div>
      <ChangeLogList
        runs={data.redline.changed_runs}
        emptyText={`No text changed in this save (compared with ${data.redline.baseline_label}).`}
      />
    </div>
  );
}

export default DocumentViewPage;
